import DateTime, { type ITimeZone } from '@dealroadshow/uikit-date-time';
import { UTC_TIME_ZONE } from '@framework/timezone/constants';

/** `nextStart` is for exclusive upper bounds (`< to`); `start`/`end` for inclusive ranges. */
type TDayBound = 'start' | 'end' | 'nextStart';

/** Naive date ("2026-05-21"), any ISO string, or a picker `Date` carrying the wall clock locally. */
type TDayInput = string | Date | null | undefined;

const toCalendarParts = (date: TDayInput): [number, number, number] | null => {
  if (!date) {
    return null;
  }

  if (date instanceof Date) {
    return Number.isNaN(date.getTime())
      ? null
      : [date.getFullYear(), date.getMonth() + 1, date.getDate()];
  }

  const [year, month, day] = date.slice(0, 10).split('-').map(Number);

  return year && month && day ? [year, month, day] : null;
};

/**
 * The single conversion point for when a calendar day begins or ends in `timeZone`.
 *
 * The wall clock is built explicitly because `new DateTime(date, { timeZone })` parses strings
 * as UTC midnight, and the date-components constructor ignores `timeZone` altogether - both are
 * wrong by a day.
 */
const zonedDayBound = (
  date: TDayInput,
  bound: TDayBound,
  timeZone: ITimeZone,
): DateTime | null => {
  const parts = toCalendarParts(date);

  if (!parts) {
    return null;
  }

  const [year, month, day] = parts;
  const dt = new DateTime(0, { timeZone }).set({
    year,
    month: month - 1,
    date: day,
    hours: 12,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  if (bound === 'nextStart') {
    return dt.addDays(1).setStartOfDay();
  }

  return bound === 'start' ? dt.setStartOfDay() : dt.setEndOfDay();
};

/** ISO 8601 instant - the preferred wire format (FIN-21745). Used by the bond screeners. */
export const toApiDate = (
  date: TDayInput,
  bound: TDayBound,
  timeZone: ITimeZone = UTC_TIME_ZONE,
): string | null => zonedDayBound(date, bound, timeZone)?.ISOString ?? null;

/** Unix seconds - legacy, only for RPCs whose contract still takes a timestamp (FIN-21745). */
export const toApiTimestamp = (
  date: TDayInput,
  bound: TDayBound,
  timeZone: ITimeZone,
): number | null => zonedDayBound(date, bound, timeZone)?.timestampInSeconds ?? null;

/**
 * Widens an inclusive end-of-day into the exclusive bound trace needs: it binds `dateTo` as a
 * Doctrine DATE and compares with `<`, so end-of-day truncates to the selected day and excludes
 * it.
 */
export const toExclusiveDayUpperBound = (unixSeconds: number | null | undefined): number | null => (
  unixSeconds
    ? toApiTimestamp(new Date(unixSeconds * 1000).toISOString(), 'nextStart', UTC_TIME_ZONE)
    : null
);
